import * as Sentry from '@sentry/react';
import 'firebase/firestore';
import 'firebase/auth';
import NavBar from "./components/common/NavBar";
import { Route, Routes, Navigate } from "react-router-dom";
import './App.css';
import Confirmation from "./components/cakes/Confirmation";
import CakeOrder from "./components/cakes/CakeOrder";
import RoscaOrderForm from './components/roscas/RoscasOrderForm';
import RoscaOrders from './components/roscas/RoscaOrders';

import BreadForm from './components/bread/BreadForm';
import BreadOrder from './components/bread/BreadOrder';
import Create from "./components/common/Create";
import Search from "./components/common/Search";
import BakersView from './components/bakers/BakersView';
import BakersViewV2 from './components/bakers-v2/BakersViewV2';

import {useAuthState} from "react-firebase-hooks/auth";
import {auth} from "./firebase/firebase";
import { lazy, Suspense, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomOrders from './components/custom-orders/CustomOrders';
import MerchantOrders from './components/merchant-orders/MerchantOrders';
import HQProductManager from './components/merchant-orders/HQProductManager';
import ConsolidationView from './components/merchant-orders/ConsolidationView';
import Analytics from './components/analytics/Analytics';
import JobApplications from './components/job-applications/JobApplications';

// Lazy-loaded: fabric.js is heavy and only needed inside the designer
const TopperDesigner = lazy(() => import('./components/topper/TopperDesigner'));
import userService from './services/user.service';
import Settings from './components/settings/Settings';
import { StoreProvider } from './context/StoreContext';
import { getStoreLabel } from './constants/stores';
import { setUserProperties } from './services/analytics.service';
import { getDefaultRoute, getRolePermissions } from './constants/roles';

function SplashScreen() {
  return (
    <>
      <style>{`
        @keyframes splashFloatUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes splashFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes splashShimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .splash-emoji {
          font-size: 48px;
          line-height: 1;
          animation: splashFloatUp 0.4s ease-out both;
        }
        .splash-title {
          color: #d4a843;
          font-family: Georgia, 'Times New Roman', serif;
          font-size: 1.6rem;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          margin: 0;
          animation: splashFadeIn 0.3s ease-out 0.2s both;
        }
        .splash-shimmer-track {
          width: 120px;
          height: 2px;
          background: rgba(212, 168, 67, 0.15);
          border-radius: 2px;
          overflow: hidden;
          position: relative;
          animation: splashFadeIn 0.3s ease-out 0.3s both;
        }
        .splash-shimmer-bar {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, #d4a843, transparent);
          animation: splashShimmer 1.4s ease-in-out infinite;
        }
      `}</style>
      <div className="splash-emoji" aria-hidden="true">🥐</div>
      <p className="splash-title">Los Portales</p>
      <div className="splash-shimmer-track">
        <div className="splash-shimmer-bar" />
      </div>
    </>
  );
}

function ShieldCheckIcon() {
  return (
    <svg width="58" height="58" viewBox="0 0 58 58" fill="none" aria-hidden="true">
      <path
        d="M29 6L47 12.5V25C47 36.038 39.852 45.953 29 49C18.148 45.953 11 36.038 11 25V12.5L29 6Z"
        fill="rgba(255,255,255,0.7)"
        stroke="#1f1f1f"
        strokeWidth="2"
      />
      <path
        d="M21 28L27 34L37 22"
        stroke="#C43B21"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AccessDenied() {
  return (
    <div className="basicContainer" style={{ padding: '4rem 1rem', background: '#f7f4f1' }}>
      <div
        style={{
          background: '#fff',
          borderRadius: '22px',
          padding: '2.75rem 2.25rem',
          color: '#1f1f1f',
          maxWidth: '420px',
          margin: '0 auto',
          boxShadow: '0 22px 44px rgba(22, 22, 22, 0.12)',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            width: '92px',
            height: '92px',
            borderRadius: '24px',
            background: 'linear-gradient(135deg, #FAD8AF, #E7A55B)',
            margin: '0 auto 1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ShieldCheckIcon />
        </div>
        <p className="eyebrow" style={{ textTransform: 'uppercase', letterSpacing: '0.08em', color: '#c18c5b' }}>
          Seguridad activa
        </p>
        <h2 style={{ margin: '0.4rem 0 0', fontSize: '1.9rem' }}>Acceso restringido</h2>
        <p style={{ marginTop: '0.75rem', lineHeight: 1.5, color: '#4a4a4a' }}>
          Tu rol actual no permite entrar aquí. Contacta a gerencia si necesitas ampliar tus permisos.
        </p>
        <button
          type="button"
          style={{
            marginTop: '1.75rem',
            padding: '0.85rem 1.9rem',
            borderRadius: '999px',
            background: '#1f1f1f',
            border: 'none',
            color: '#fff',
            fontWeight: 600,
            letterSpacing: '0.05em',
            cursor: 'default',
          }}
        >
          🔐 Permisos requeridos
        </button>
      </div>
    </div>
  );
}

export default function ProtectedRoutes() {
  const [user, authLoading] = useAuthState(auth);
  const navigate = useNavigate();
  const [canManageCustomOrders, setCanManageCustomOrders] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [userRoles, setUserRoles] = useState([]);
  const [userStoreId, setUserStoreId] = useState('');
  const [permissionsError, setPermissionsError] = useState(null);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate('/sign-in');
      setCanManageCustomOrders(false);
      setPermissionsLoading(false);
      return;
    }

    let isMounted = true;

    async function fetchPermissions() {
      setPermissionsLoading(true);
      try {
        const data = await userService.getUserData(user.uid);
        if (!isMounted) return;

        const roles = Array.isArray(data?.roles) ? data.roles : [];
        const normalizedRoles = roles
          .map((role) => (typeof role === 'string' ? role.toLowerCase() : ''))
          .filter(Boolean);

        setCanManageCustomOrders(Boolean(data?.customOrdersEnabled));
        setUserRoles(normalizedRoles);
        setUserStoreId(data?.store_id || '');

        setUserProperties({
          user_role: normalizedRoles.join(',') || 'unknown',
          store_id: data?.store_id || 'unknown',
          custom_orders_enabled: data?.customOrdersEnabled ? 'true' : 'false',
        });
      } catch (error) {
        console.error('Error fetching user permissions', error);
        Sentry.captureException(error, {
          tags: { component: 'ProtectedRoute', errorType: 'permissions-fetch' },
          extra: { userId: user?.uid },
        });
        if (!isMounted) return;

        setCanManageCustomOrders(false);
        setUserRoles([]);
        setUserStoreId('');
        setPermissionsError(error);
      } finally {
        if (isMounted) {
          setPermissionsLoading(false);
        }
      }
    }

    fetchPermissions();
    return () => {
      isMounted = false;
    };
  }, [user, authLoading, navigate]);

  const rolePermissions = getRolePermissions(userRoles);
  const customOrdersAccess = canManageCustomOrders && rolePermissions.canViewCustomOrders;

  const renderWithAccess = (canAccess, element) => (canAccess ? element : <AccessDenied />);

  if (authLoading || permissionsLoading) {
    return (
      <div className="auth-splash">
        <SplashScreen />
      </div>
    );
  }

  if (!user) return null;

  if (permissionsError) {
    const isUserNotFound = permissionsError.message === 'User document not found';
    return (
      <div className="basicContainer" style={{ padding: '4rem 1rem', background: '#f7f4f1' }}>
        <div style={{
          background: '#fff', borderRadius: '22px', padding: '2.75rem 2.25rem',
          color: '#1f1f1f', maxWidth: '420px', margin: '0 auto',
          boxShadow: '0 22px 44px rgba(22, 22, 22, 0.12)', textAlign: 'center',
        }}>
          {isUserNotFound ? (
            <>
              <h2 style={{ fontSize: '1.9rem' }}>🔒 Cuenta no configurada</h2>
              <p style={{ marginTop: '0.75rem', lineHeight: 1.5, color: '#4a4a4a' }}>
                Tu cuenta existe pero aún no ha sido activada en el sistema. Pide a un gerente que te asigne una tienda y un rol.
              </p>
              <button type="button" onClick={() => { auth.signOut(); navigate('/sign-in'); }} style={{
                marginTop: '1.75rem', padding: '0.85rem 1.9rem', borderRadius: '999px',
                background: '#1f1f1f', border: 'none', color: '#fff', fontWeight: 600,
                cursor: 'pointer',
              }}>
                ↩ Cerrar sesión
              </button>
            </>
          ) : (
            <>
              <h2 style={{ fontSize: '1.9rem' }}>⚠️ Error de conexión</h2>
              <p style={{ marginTop: '0.75rem', lineHeight: 1.5, color: '#4a4a4a' }}>
                No pudimos verificar tus permisos. Revisa tu conexión e intenta de nuevo.
              </p>
              <button type="button" onClick={() => window.location.reload()} style={{
                marginTop: '1.75rem', padding: '0.85rem 1.9rem', borderRadius: '999px',
                background: '#1f1f1f', border: 'none', color: '#fff', fontWeight: 600,
                cursor: 'pointer',
              }}>
                🔄 Reintentar
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <StoreProvider userRoles={userRoles} assignedStoreId={userStoreId}>
      <NavBar
        rolePermissions={rolePermissions}
        canManageCustomOrders={customOrdersAccess}
      />
      <Routes>
        <Route
          path="/"
          element={
            getDefaultRoute(userRoles) !== '/'
              ? <Navigate to={getDefaultRoute(userRoles)} replace />
              : renderWithAccess(rolePermissions.canViewCreate, <Create />)
          }
        />
        <Route path="/search" element={renderWithAccess(rolePermissions.canViewSearch, <Search />)} />
        <Route path="/success" element={renderWithAccess(rolePermissions.canViewCreate, <Confirmation />)} />
        <Route path="/search/:id" element={renderWithAccess(rolePermissions.canViewSearch, <CakeOrder />)} />
        <Route path="/roscas" element={renderWithAccess(rolePermissions.canViewCreate, <RoscaOrderForm />)} />
        <Route path="/roscas/roscaOrders" element={renderWithAccess(rolePermissions.canViewCreate, <RoscaOrders />)} />
        <Route path="/panaderos" element={renderWithAccess(rolePermissions.canViewBakers, <BakersViewV2 />)} />
        <Route path="/panaderos-legacy" element={renderWithAccess(rolePermissions.canViewBakers, <BakersView />)} />
        <Route path="/panes" element={renderWithAccess(rolePermissions.canViewCreate, <BreadForm />)} />
        <Route path="/panes/:id" element={renderWithAccess(rolePermissions.canViewCreate, <BreadOrder />)} />
        <Route
          path="/custom-orders"
          element={
            <CustomOrders
              canManageCustomOrders={customOrdersAccess}
              permissionsLoading={permissionsLoading}
              userStore={userStoreId}
            />
          }
        />
        <Route path="/settings" element={renderWithAccess(rolePermissions.canViewSettings, <Settings userRoles={userRoles} />)} />
        <Route
          path="/merchant-orders"
          element={
            <MerchantOrders
              canViewMerchantOrders={rolePermissions.canViewMerchantOrders}
              permissionsLoading={permissionsLoading}
              userStoreId={userStoreId}
              storeName={getStoreLabel(userStoreId)}
            />
          }
        />
        <Route
          path="/product-catalog"
          element={renderWithAccess(
            rolePermissions.canConsolidateOrders,
            <div className="basicContainer merchant-orders-container">
              <div className="merchant-orders-wrapper">
                <HQProductManager />
              </div>
            </div>
          )}
        />
        <Route
          path="/hq-consolidation"
          element={renderWithAccess(
            rolePermissions.canConsolidateOrders,
            <div className="basicContainer merchant-orders-container">
              <div className="merchant-orders-wrapper">
                <ConsolidationView />
              </div>
            </div>
          )}
        />
        <Route
          path="/analytics"
          element={renderWithAccess(rolePermissions.canViewAnalytics, <Analytics />)}
        />
        <Route
          path="/job-applications"
          element={renderWithAccess(rolePermissions.canViewJobApplications, <JobApplications />)}
        />
        <Route
          path="/topper"
          element={renderWithAccess(
            rolePermissions.canDesignToppers,
            <Suspense fallback={<div className="auth-splash"><SplashScreen /></div>}>
              <TopperDesigner />
            </Suspense>
          )}
        />
        <Route
          path="/topper/:orderId"
          element={renderWithAccess(
            rolePermissions.canDesignToppers,
            <Suspense fallback={<div className="auth-splash"><SplashScreen /></div>}>
              <TopperDesigner />
            </Suspense>
          )}
        />
      </Routes>
    </StoreProvider>
  );
}
